/**
 * Handles the final pipeline step: updating J-code documents in Postgres
 * after a successful Firebase upload. Fires as a side-effect when
 * `downloadURL` becomes available while uploading.
 * @module useJCodeDocUpdate
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { updateAllJCodeDocuments } from "../../../../api/postgreSQL/postCalls/updateJCodeDocuments";
import { runAuthLetterAgentRerun } from "../../../../api/services/authLetterAgentRerunService";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { getViewerDocumentTitleByDocType } from "../../../../constants/statusToDocMap/statusToDocMap";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import {
  AuthOnFile,
  AuthRangeResponse,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { SiblingOrdersResponse } from "../../../../data-model/siblingOrdersModel";
import { DocumentTypesForOrder } from "../../../../enums/documentTypes";
import { setAuthLetterExtractionLoading } from "../../../../redux/slice/authLetterExtractionSlice";
import { resetFileUploadState } from "../../../../redux/slice/fileUploadSlice";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import {
  insertDocumentUploadedToOrderTrailComment,
  resolveJCodeLabelsForTrail,
} from "../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { showApiError } from "../../../../utils/showApiError";
import {
  removeAuthLetterDocByPath,
  updateJcodesWithCorrectDocRef,
} from "../../../../utils/updateJcodesWithCorrectDocRef";
import { NarLetterModalMetaData } from "../types";
import { resolveSelectedJCodeDetails } from "../utils/generateApprovalLetterDocumentTitle";
import { generateDocumentUpdatePayloads } from "../utils/generateDocumentUpdatePayloads";
import { applyAuthLetterUploadedReviewStatus } from "../utils/syncAuthLetterReviewStatusToCptTables";

/** Side-effect inputs for `useJCodeDocUpdate` (upload path, merge flags, callbacks, dispatch). */
interface UseJCodeDocUpdateParams {
  authOnFileDetails: AuthOnFile | null | undefined;
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  selectedMedicines: string[];
  metaData: NarLetterModalMetaData | undefined;
  fileUploadPath: string;
  documentTitle: string;
  userEmail: string;
  /** In-memory uploaded letter (merged PDF or single file) for the one-time agent rerun. */
  fileForRerun: File | null;
  /** Payer name used for the one-time agent rerun (from the single-order layout context). */
  payerName: string;
  isUploading: boolean;
  setIsUploading: (v: boolean) => void;
  isMergingRef: MutableRefObject<boolean>;
  downloadURL: string | null;
  abortControllerRef: MutableRefObject<AbortController | null>;
  isLetterSelectedFromDropdownAtSaveRef: MutableRefObject<boolean>;
  onDocUploaded: (
    docTitle: string,
    docPath: string,
    isLetterSelectedFromDropdown: boolean,
  ) => void;
  onSuccess?: (id: string, docPath: string, documentTitle: string) => void;
  onAgentRerunStatusChange?: (
    isRunning: boolean,
    jCodeId: string,
    docPath: string,
  ) => void;
  dispatch: AppDispatch;
}

/**
 * Strips the incorrect letter (if any) and attaches the newly uploaded doc
 * to the matching J-codes.
 */
const buildUpdatedEntries = (
  authOnFileDetails: AuthOnFile | null | undefined,
  selectedMedicines: string[],
  metaData: NarLetterModalMetaData | undefined,
  fileUploadPath: string,
  documentTitle: string,
  userEmail: string,
): AuthRangeResponse[] => {
  let authEntries = authOnFileDetails?.entries ?? [];

  if (metaData?.incorrectLetterDocPath) {
    authEntries = removeAuthLetterDocByPath(
      authEntries,
      selectedMedicines,
      metaData.incorrectLetterDocPath,
    );
  }

  return updateJcodesWithCorrectDocRef(
    authEntries,
    selectedMedicines,
    metaData?.docType ?? "",
    fileUploadPath,
    documentTitle,
    userEmail,
  );
};

/**
 * Returns whether the current upload is an auth-letter replacement or new auth-letter.
 */
const isAuthLetterUpdate = (
  metaData: NarLetterModalMetaData | undefined,
): boolean =>
  !!(
    metaData?.incorrectLetterDocPath ||
    metaData?.docType === DocumentTypesForOrder.AUTH_LETTER
  );

/**
 * Persists the updated J-code document references to Postgres.
 */
const persistJCodeDocuments = async (
  entries: AuthRangeResponse[],
  orderId: string,
  orgId: string,
  parentOrderAuthOnFile: AuthOnFile,
  filteredSiblingOrdersDetails: SiblingOrdersResponse,
  signal: AbortSignal,
) => {
  return updateAllJCodeDocuments(
    generateDocumentUpdatePayloads(entries, orderId, orgId),
    parentOrderAuthOnFile,
    filteredSiblingOrdersDetails,
    signal,
  );
};

/**
 * Builds updated auth entries and pushes document updates to Postgres
 * when the Firebase download URL becomes available.
 *
 * @param params - Auth data, upload state, abort ref, and callbacks.
 */
export const useJCodeDocUpdate = ({
  authOnFileDetails,
  generalOrderDetails,
  selectedMedicines,
  metaData,
  fileUploadPath,
  documentTitle,
  userEmail,
  fileForRerun,
  payerName,
  isUploading,
  setIsUploading,
  isMergingRef,
  downloadURL,
  abortControllerRef,
  isLetterSelectedFromDropdownAtSaveRef,
  onDocUploaded,
  onSuccess,
  onAgentRerunStatusChange,
  dispatch,
}: UseJCodeDocUpdateParams) => {
  const { fetchAuthOnFileDetails, filteredSiblingOrders } =
    useDataAccumulatorForSingleOrder();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const updateJCodesWithCorrectDocData = useCallback(async () => {
    const orderId = generalOrderDetails?.order_id ?? "";
    const orgId = getOrgIdForFetchExternalWorklist() ?? "";

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let updatedEntries = buildUpdatedEntries(
      authOnFileDetails,
      selectedMedicines,
      metaData,
      fileUploadPath,
      documentTitle,
      userEmail,
    );
    let modalClosedBeforeAgentRerun = false;

    if (isAuthLetterUpdate(metaData)) {
      try {
        updatedEntries = await applyAuthLetterUploadedReviewStatus(
          updatedEntries,
          selectedMedicines,
          orderId,
          orgId,
          filteredSiblingOrders,
          controller.signal,
        );
      } catch (error) {
        console.error("Failed to update auth letter review status:", error);
      }
    }

    try {
      await persistJCodeDocuments(
        updatedEntries,
        orderId,
        orgId,
        authOnFileDetails ?? {},
        filteredSiblingOrders ?? { siblings: [] },
        controller.signal,
      );

      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";

      const authEntries =
        authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries ?? [];

      insertDocumentUploadedToOrderTrailComment({
        orderId,
        orgId,
        patientId:
          generalOrderDetails?.demographics?.patient_id ??
          generalOrderDetails?.demographics?.patient_mrn,
        providerEmail: user?.email ?? userEmail,
        providerName: actingProviderName,
        jCodeLabels: resolveJCodeLabelsForTrail(selectedMedicines, authEntries),
        documentLabel: getViewerDocumentTitleByDocType(metaData?.docType ?? ""),
      });

      // Re-extract every newly uploaded auth letter so the backend persists
      // the latest values before the order's auth-on-file data is refreshed.
      if (isAuthLetterUpdate(metaData) && fileForRerun) {
        const selectedJCodeDetails = resolveSelectedJCodeDetails(
          authOnFileDetails,
          selectedMedicines,
        );
        const jCodeValues = selectedJCodeDetails
          .map((j) => j.j_code ?? "")
          .filter((code) => code.trim().length > 0);
        if (jCodeValues.length > 0) {
          dispatch(
            setAuthLetterExtractionLoading({
              orderId,
              documentPath: fileUploadPath,
              jCodeIds: selectedMedicines,
              documentTitle,
              isLoading: true,
            }),
          );
          onAgentRerunStatusChange?.(true, metaData?.id ?? "", fileUploadPath);
          onDocUploaded(
            documentTitle,
            fileUploadPath,
            isLetterSelectedFromDropdownAtSaveRef.current,
          );
          toast.success(ToastMessages.NAR_LETTER_UPLOADED);
          dispatch(resetFileUploadState());
          onSuccess?.(metaData?.id ?? "", fileUploadPath, documentTitle);
          dispatch(closeModal());
          modalClosedBeforeAgentRerun = true;
          try {
            await runAuthLetterAgentRerun({
              file: fileForRerun,
              payerName,
              orderId,
              orgId,
              jCodes: jCodeValues,
              medicineNames: selectedJCodeDetails
                .map((j) => j.medicine_name ?? j.description ?? "")
                .filter((name) => name.trim().length > 0),
            });
            await fetchAuthOnFileDetails(orderId, orgId, {
              hideLoader: true,
            });
          } catch (error) {
            console.error("Auth letter agent rerun failed:", error);
            toast.error(ToastMessages.RERUN_ERROR);
          } finally {
            dispatch(
              setAuthLetterExtractionLoading({
                orderId,
                documentPath: fileUploadPath,
                jCodeIds: selectedMedicines,
                documentTitle,
                isLoading: false,
              }),
            );
            onAgentRerunStatusChange?.(
              false,
              metaData?.id ?? "",
              fileUploadPath,
            );
          }
        }
      }

      if (modalClosedBeforeAgentRerun) {
        return;
      }

      onDocUploaded(
        documentTitle,
        fileUploadPath,
        isLetterSelectedFromDropdownAtSaveRef.current,
      );
      toast.success(ToastMessages.NAR_LETTER_UPLOADED);
      dispatch(resetFileUploadState());
      onSuccess?.(metaData?.id ?? "", fileUploadPath, documentTitle);
      dispatch(closeModal());
    } catch (error) {
      console.error("Failed to update auth entries:", error);
      showApiError(error, ToastMessages.NAR_LETTER_UPLOAD_ERROR);
    } finally {
      setIsUploading(false);
      isMergingRef.current = false;
    }
  }, [
    authOnFileDetails,
    generalOrderDetails,
    selectedMedicines,
    metaData?.docType,
    metaData?.id,
    metaData?.incorrectLetterDocPath,
    fileUploadPath,
    documentTitle,
    userEmail,
    fileForRerun,
    payerName,
    onDocUploaded,
    onSuccess,
    onAgentRerunStatusChange,
    dispatch,
    abortControllerRef,
    setIsUploading,
    isMergingRef,
    fetchAuthOnFileDetails,
    filteredSiblingOrders,
    providerDetails,
    user?.email,
  ]);

  const updateRef = useRef(updateJCodesWithCorrectDocData);
  useEffect(() => {
    updateRef.current = updateJCodesWithCorrectDocData;
  });

  useEffect(() => {
    if (downloadURL && isUploading) {
      updateRef.current();
    }
  }, [downloadURL, isUploading]);
};
